import { useCallback, useRef } from 'react';
import { graphql } from 'react-relay';
import { useIntl, defineMessages } from 'react-intl';

import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { type FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import { useContentTreeSelectionActions } from './store.ts';
import type { ContentId, ContentTreeItem } from './contentTreeItemType.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useLazyQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useLazyQueryInterop.ts';
import type { useSelectionCountContentMetadataQuery$data } from './__generated__/useSelectionCountContentMetadataQuery.graphql.ts';
const ContentMetadataQuery = graphql`
	query useSelectionCountContentMetadataQuery($contentIds: [ID!]!) {
		pageContent: content(ids: $contentIds, type: "page") {
			nodes {
				id
				visibleDescendantsCount
			}
		}
		whiteboardContent: content(ids: $contentIds, type: "whiteboard") {
			nodes {
				id
				visibleDescendantsCount
			}
		}
		databaseContent: content(ids: $contentIds, type: "database") {
			nodes {
				id
				visibleDescendantsCount
			}
		}
		embedContent: content(ids: $contentIds, type: "embed") {
			nodes {
				id
				visibleDescendantsCount
			}
		}
		folderContent: content(ids: $contentIds, type: "folder") {
			nodes {
				id
				visibleDescendantsCount
			}
		}
		slideContent: content(ids: $contentIds, type: "slide") {
			nodes {
				id
				visibleDescendantsCount
			}
		}
	}
`;

type SelectCountArgs = {
	item: ContentTreeItem; // Current item for which the count is being updated.
	ancestorIds: ContentId[]; // Ancestor IDs of the current item.
	isExplicitlySelected: boolean; // Whether the current item is explicitly selected.
	countToRemove: number; // Count of previously selected descendants that need to be removed from the total count.
	flags: FlagsStateContainer;
	contentForCount: ContentId[]; // IDs passed to the query, used to clear loading state on error.
} | null;

export const useSelectionCount = (): ((
	item: ContentTreeItem,
	ancestorIds: ContentId[],
	contentForCount: ContentId[],
	isExplicitlySelected: boolean,
	countToRemove: number,
	flags: FlagsStateContainer,
) => void) => {
	const {
		updateSelectedDescendantsCount,
		updateTotalCount,
		addLoadingDescendant,
		removeLoadingDescendant,
		incrementAncestors,
		updateParent,
		getState,
	} = useContentTreeSelectionActions();

	const intl = useIntl();
	const latestArgsRef = useRef<SelectCountArgs>(null);

	const [fetchMetadata] = useLazyQueryInterop<useSelectionCountContentMetadataQuery$data>(
		ContentMetadataQuery,
		{
			fetchPolicy: 'network-only',
			onCompleted: (data) => {
				if (latestArgsRef.current) {
					const nodes = [
						...(data?.pageContent?.nodes ?? []),
						...(data?.whiteboardContent?.nodes ?? []),
						...(data?.databaseContent?.nodes ?? []),
						...(data?.embedContent?.nodes ?? []),
						...(data?.folderContent?.nodes ?? []),
						...(data?.slideContent?.nodes ?? []),
					];

					const { item, ancestorIds, isExplicitlySelected, countToRemove } = latestArgsRef.current;

					const itemId = item.id;

					const { selectedContent } = getState();
					const isCurrentlySelected = !!selectedContent[itemId];
					const isParentSelected = item.parentId && item.parentId in selectedContent;
					let selectedCount = 0;

					nodes.forEach((node) => {
						if (!node || !node.id) {
							return;
						}
						if (node.id === itemId) {
							selectedCount = node.visibleDescendantsCount;
						}
						if (isCurrentlySelected) {
							updateSelectedDescendantsCount(node.id, node.visibleDescendantsCount);
						}
						removeLoadingDescendant(node.id);
					});

					// Only update counts if content is explicitly selected.
					if (isExplicitlySelected && isCurrentlySelected) {
						if (isParentSelected && typeof item.parentId === 'string') {
							// If the parent is selected, we need to update the excludedChildren array.
							updateParent(itemId, item.parentId);
						}

						incrementAncestors([...ancestorIds], selectedCount, countToRemove);

						updateTotalCount(selectedCount + 1 - countToRemove); // +1 for the content itself
					} else {
						if (countToRemove > 0) {
							// Remove count that have been previously addressed.
							updateTotalCount(-countToRemove);
						}
					}
				}
			},
			onError: (err) => {
				markErrorAsHandled(err);
				getMonitoringClient().submitError(err, {
					attribution: Attribution.DISCO,
				});

				latestArgsRef.current?.contentForCount.forEach((id) => removeLoadingDescendant(id));

				if (latestArgsRef.current) {
					void latestArgsRef.current.flags.showErrorFlag({
						title: intl.formatMessage(i18n.flagErrorTitle),
						description: intl.formatMessage(i18n.flagErrorDescription),
					});
				}
			},
		},
	);

	return useCallback(
		(
			item: ContentTreeItem,
			ancestorIds: ContentId[],
			contentForCount: ContentId[],
			isExplicitlySelected: boolean,
			countToRemove: number,
			flags: FlagsStateContainer,
		) => {
			latestArgsRef.current = {
				item,
				ancestorIds,
				isExplicitlySelected,
				countToRemove,
				flags,
				contentForCount,
			};

			contentForCount.forEach((id) => addLoadingDescendant(id));
			fetchMetadata({ variables: { contentIds: contentForCount } });
		},
		[addLoadingDescendant, fetchMetadata],
	);
};

export const i18n: {
	flagErrorTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	flagErrorDescription: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	flagErrorTitle: {
		id: 'content-tree-actions.selection-count.failed.title',
		defaultMessage: 'Something went wrong',
		description: 'Error flag title displayed when content tree selection count loading fails',
	},
	flagErrorDescription: {
		id: 'content-tree-actions.selection-count.failed.description',
		defaultMessage:
			'Refresh and try again. If this keeps happening, contact your space administrator.',
		description:
			'A generic error message that displays in a flag to let the user know that there was a problem while loading their tree',
	},
});
